import { format, parseISO } from 'date-fns';

import type {
  BarberScheduleResponse,
  ScheduleTimeSlot,
} from '@/api/booking/getBookingScheduleQuery';
import type {
  BarberCalendarData,
  BarberSchedule,
} from '@/types/barberCalendar';

import { isWorkdayEnabledOnDate } from './barberScheduleUtils';
import { parseDate } from './dateUtils';

export interface TimeSlot {
  time: Date;
  available: boolean;
}

export function parseScheduleResponse(
  response: BarberScheduleResponse[],
  selectedDate: Date,
): TimeSlot[] {
  const dateKey = format(selectedDate, 'yyyy-MM-dd');

  const daySchedule = response.find((schedule) => schedule.day === dateKey);

  if (!daySchedule) {
    return [];
  }

  const allSlots: TimeSlot[] = [];

  const periods: Array<'morning' | 'afternoon' | 'evening'> = [
    'morning',
    'afternoon',
    'evening',
  ];

  periods.forEach((period) => {
    const periodSlots = daySchedule.times[period] || [];
    periodSlots.forEach((slot: { time: string; available: boolean }) => {
      allSlots.push({
        time: new Date(slot.time),
        available: slot.available,
      });
    });
  });

  return allSlots;
}

export function getAvailabilityForDate(
  response: BarberScheduleResponse[],
  date: Date,
): number {
  const dateKey = format(date, 'yyyy-MM-dd');

  const daySchedule = response.find((schedule) => schedule.day === dateKey);

  if (!daySchedule) {
    return 0;
  }

  return daySchedule.availability;
}

export const AVAILABILITY_WAITLIST_ONLY = -1;

export function getIsDayEnabledFromSchedules(
  schedules: BarberSchedule[] | undefined,
  day: string,
): boolean {
  if (!schedules) return false;

  const date = parseISO(day);

  return schedules.some(
    (schedule) =>
      isWorkdayEnabledOnDate(schedule.workdays, date) &&
      (schedule.startDate ?? '') <= day &&
      (!schedule.endDate || day <= schedule.endDate),
  );
}

export function getIsDayEnabledFromBarber(
  barber: BarberCalendarData,
  day: string,
): boolean {
  const isDayOff =
    barber.daysOff?.some((d) => d.wholeDay && d.day.startsWith(day)) ?? false;
  if (isDayOff) return false;

  const isDayOn = barber.daysOn?.some((d) => d.day.startsWith(day)) ?? false;
  if (isDayOn) return true;

  return getIsDayEnabledFromSchedules(barber.schedules, day);
}

export function buildAvailabilityMap(
  response: {
    guestId: string;
    data?: BarberScheduleResponse[];
    barber?: BarberCalendarData;
  }[],
  options?: { waitlistEnabled?: boolean },
): Map<string, number> {
  const map = new Map<string, number>();
  const { waitlistEnabled = false } = options || {};

  const allDates = response
    .flatMap((r) => r.data || [])
    .map((r) => format(parseDate(r.day), 'yyyy-MM-dd'));

  allDates.forEach((date) => {
    const availableForAllGuests = response.every((r) => {
      const { availability = 0 } = r.data?.find((d) => d.day === date) || {};
      return availability > 0;
    });

    const barberWorksOnDay = response.every((r) => {
      if (r.barber) {
        return getIsDayEnabledFromBarber(r.barber, date);
      }
      // Fallback when barber calendar data is unavailable
      const daySchedule = r.data?.find((d) => d.day === date);
      if (!daySchedule) return false;
      return (
        daySchedule.times.morning.length > 0 ||
        daySchedule.times.afternoon.length > 0 ||
        daySchedule.times.evening.length > 0
      );
    });

    if (availableForAllGuests) {
      map.set(date, 1);
    } else if (waitlistEnabled && barberWorksOnDay) {
      // Mark as waitlist-only (selectable but with different styling)
      map.set(date, AVAILABILITY_WAITLIST_ONLY);
    } else {
      map.set(date, 0);
    }
  });

  return map;
}

export function limitTimeSlots(slots: TimeSlot[], max: number): TimeSlot[] {
  if (max <= 0) {
    return [];
  }

  return slots.slice(0, max);
}

export function filterSlotBarberIds(
  slot: ScheduleTimeSlot,
  disallowedBarberIds: Set<string>,
): ScheduleTimeSlot {
  if (!slot.barberIds || slot.barberIds.length === 0) return slot;

  const filteredBarberIds = slot.barberIds.filter(
    (id) => !disallowedBarberIds.has(id),
  );

  return {
    ...slot,
    barberIds: filteredBarberIds,
    available: filteredBarberIds.length === 0 ? false : slot.available,
  };
}

export function filterScheduleForMultipleServices(
  scheduleData: BarberScheduleResponse[] | undefined,
  disallowedBarberIds: Set<string>,
): BarberScheduleResponse[] | undefined {
  if (!scheduleData) return scheduleData;

  return scheduleData.map((day) => {
    const filteredTimes = {
      morning: day.times.morning.map((slot) =>
        filterSlotBarberIds(slot, disallowedBarberIds),
      ),
      afternoon: day.times.afternoon.map((slot) =>
        filterSlotBarberIds(slot, disallowedBarberIds),
      ),
      evening: day.times.evening.map((slot) =>
        filterSlotBarberIds(slot, disallowedBarberIds),
      ),
    };

    const hasAnyAvailableSlot = [
      ...filteredTimes.morning,
      ...filteredTimes.afternoon,
      ...filteredTimes.evening,
    ].some((slot) => slot.available);

    return {
      ...day,
      availability: hasAnyAvailableSlot ? day.availability : 0,
      suggestedTimes: day.suggestedTimes.map((slot) =>
        filterSlotBarberIds(slot, disallowedBarberIds),
      ),
      times: filteredTimes,
    };
  });
}
